import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { Box, ChevronDownIcon, ChevronUpIcon, Divider, HStack, IconButton, Pressable, ScrollView, useTheme, View } from 'native-base';
import { findIndex } from 'lodash';

import { Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { useSalesLaborTracker } from '../context/sales-labor-tracker.context';
import { DaypartCard } from './daypart-card';
import { DaypartTab } from './daypart-tab';
import { LayoutChangeEvent } from 'react-native';

const DIVIDER = 9; // is the total width of the divider between regular daypart and total
const CARD_LAYOUT = { width: 210, height: 112 };

type DaypartListViewProps = {};

export const DaypartListView = (props: DaypartListViewProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  const scrollRef = useRef<typeof ScrollView>();
  const scrolledRef = useRef<boolean>();
  const isExpanded = useBoolean(true);
  const [tileWidth, setTileWidth] = useState<number>(0);
  const [cardHeight, setCardHeight] = useState<number>(0);

  const sales = useSalesLaborTracker(_ => _.sales);
  const displayForecastedSales = useSalesLaborTracker(_ => _.displayForecastedSales);
  const dataThrough = useSalesLaborTracker(_ => _.dataThrough);
  const partNumber = useSalesLaborTracker(_ => _.partNumber);
  const setPartNumber = useSalesLaborTracker(_ => _.setPartNumber);

  const numOfTiles = sales.dayparts.length + 1;

  // Reset only when the business day changes, not on every data refresh
  const firstDaypartStart = sales.dayparts[0]?.startTime?.getTime();
  useEffect(() => {
    setCardHeight(0);
  }, [firstDaypartStart]);

  const handleChangeLayout = ({ nativeEvent: { layout } }: LayoutChangeEvent) => {
    setTileWidth((layout.width - DIVIDER) / numOfTiles);
  };

  const handleCardLayout = ({ nativeEvent: { layout } }: LayoutChangeEvent) => {
    setCardHeight(prev => Math.max(prev, layout.height));
  };

  useEffect(() => {
    let timeoutRef;

    if (!scrollRef.current && scrolledRef.current) return;

    timeoutRef = setTimeout(() => {
      const index = findIndex(sales.dayparts, _ => _.partNumber === partNumber) ?? 0;
      const offsetX = tileWidth * index;
      // @ts-ignore
      scrollRef.current.scrollTo({ x: offsetX, animated: true });
      scrolledRef.current = true;
    }, 500);

    return () => {
      if (timeoutRef) clearTimeout(timeoutRef);
    };
  }, [tileWidth]);

  return (
    <View pt={1} bg="grayCool.100">
      {!isExpanded.value && (
        <HStack
          alignItems="center"
          my={1}
          mx={3}
          px={3}
          py={2}
          rounded={10}
          borderWidth={1}
          borderColor="gray.400"
          bg="white"
          style={{
            shadowColor: colors.gray[400],
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.7,
            shadowRadius: 2,
          }}
        >
          <HStack flex={1} alignItems="center" h="full">
            {sales.dayparts.map(dp => (
              <DaypartTab
                key={`daypart-tab.${dp.partNumber}`}
                isSelected={dp.partNumber === partNumber}
                title={translate(dp.shortTitle, dp.shortTitleTranslations)}
                onPress={() => setPartNumber(dp.partNumber)}
              />
            ))}

            <Divider orientation="vertical" mx={2} h="70%" bg="gray.500" />

            <DaypartTab isSelected={partNumber === undefined} title={t('sales_tracker:today')} onPress={() => setPartNumber(undefined)} />
          </HStack>

          <Pressable
            rounded="full"
            justifyContent="center"
            px={2}
            minH={7}
            borderWidth={1}
            borderColor="gray.400"
            bg="gray.100"
            onPress={() => isExpanded.setTrue()}
          >
            <HStack space={1} alignItems="center">
              <ChevronDownIcon size="14px" color="gray.800" />
              <Text size="sm" fontWeight={500} color="gray.800">
                {t('sales_tracker:show_tiles')}
              </Text>
            </HStack>
          </Pressable>
        </HStack>
      )}

      {isExpanded.value && (
        <HStack space={1.5} alignItems="center" ml={2} mr={3}>
          <HStack flex={1} alignItems="flex-start" w="full" minHeight={CARD_LAYOUT.height} onLayout={handleChangeLayout}>
            <ScrollView ref={scrollRef} flex={1} horizontal showsHorizontalScrollIndicator={Platform.OS === 'web'}>
              {sales.dayparts.map(dp => {
                return (
                  <Box key={dp.startTime?.toISOString()} p={1} minW={CARD_LAYOUT.width} w={tileWidth}>
                    <DaypartCard
                      minH={cardHeight || CARD_LAYOUT.height}
                      onLayout={handleCardLayout}
                      daypart={dp}
                      dataThrough={dataThrough}
                      displayForecastedSales={displayForecastedSales}
                      isSelected={dp.partNumber === partNumber}
                      onPress={() => setPartNumber(dp.partNumber)}
                    />
                  </Box>
                );
              })}
            </ScrollView>

            <HStack alignItems="center" w={Math.max(CARD_LAYOUT.width, tileWidth) + DIVIDER}>
              <Divider orientation="vertical" mx={1} h={cardHeight || CARD_LAYOUT.height} bg="gray.400" />
              <Box p={1} minW={CARD_LAYOUT.width} w={tileWidth}>
                <DaypartCard
                  variant="total"
                  minH={cardHeight || CARD_LAYOUT.height}
                  onLayout={handleCardLayout}
                  daypart={sales.total}
                  dataThrough={dataThrough}
                  displayForecastedSales={displayForecastedSales}
                  isSelected={partNumber === undefined}
                  onPress={() => setPartNumber(undefined)}
                />
              </Box>
            </HStack>
          </HStack>

          <IconButton
            shadow={0}
            boxSize={6}
            rounded="full"
            p={1}
            bg="white"
            _pressed={{ bg: 'gray.50' }}
            onPress={() => isExpanded.setFalse()}
            icon={<ChevronUpIcon size={4} color="gray.800" />}
          />
        </HStack>
      )}
    </View>
  );
};
